// src/features/messages/GroupInvitesBlock.tsx — "Invitations" block shown above
// the conversation list in the groups view, when the viewer has pending invites.
import { useState } from 'react';
import styled from 'styled-components';
import { Button, SectionLabel } from '@/ui';
import { useConversationInvites } from '@/hooks';

const Block = styled.div`padding: 0 4px 8px; display: flex; flex-direction: column; gap: 8px;`;
const InviteRow = styled.div`
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
`;
const Info = styled.div`flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px;`;
const Title = styled.span`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600; color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
`;
const Sub = styled.span`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;
const RowError = styled.span`
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.danger};
`;
const Actions = styled.div`display: flex; gap: 6px; flex: none;`;
const LoadErrorRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 10px 12px; border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.glassBg};
  font-family: ${({ theme }) => theme.font.body}; font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.danger};
`;

export interface GroupInvitesBlockProps {
  /** Called with the conversation id once an invite is accepted. */
  onAccepted: (conversationId: string) => void;
}

export function GroupInvitesBlock({ onAccepted }: GroupInvitesBlockProps) {
  const { invites, loading, error, refresh, accept, decline } = useConversationInvites();
  const [busyId, setBusyId] = useState<string | number | null>(null);
  const [respondErrorIds, setRespondErrorIds] = useState<Set<string | number>>(new Set());

  if (loading) return null;

  if (error) {
    return (
      <Block>
        <LoadErrorRow role="alert">
          <span>Couldn&apos;t load invitations. Retry</span>
          <Button variant="secondary" size="sm" onClick={refresh}>
            Retry
          </Button>
        </LoadErrorRow>
      </Block>
    );
  }

  if (invites.length === 0) return null;

  function markErrored(id: string | number, failed: boolean) {
    setRespondErrorIds((cur) => {
      const next = new Set(cur);
      if (failed) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function handleAccept(id: string | number) {
    setBusyId(id);
    try {
      const convo = await accept(id);
      markErrored(id, false);
      onAccepted(convo.id);
    } catch {
      markErrored(id, true);
    } finally {
      setBusyId(null);
    }
  }

  async function handleDecline(id: string | number) {
    setBusyId(id);
    try {
      await decline(id);
      markErrored(id, false);
    } catch {
      markErrored(id, true);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Block>
      <SectionLabel as="h2">Invitations</SectionLabel>
      {invites.map((inv) => {
        const busy = busyId === inv.id;
        const title = inv.conversation.title;
        return (
          <InviteRow key={inv.id}>
            <Info>
              <Title>{title}</Title>
              <Sub>invited by {inv.invitedBy?.name ?? 'someone'}</Sub>
              {respondErrorIds.has(inv.id) && (
                <RowError role="alert">Couldn&apos;t respond. Try again</RowError>
              )}
            </Info>
            <Actions>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void handleDecline(inv.id)}
                disabled={busy}
                aria-label={`Decline invite to ${title}`}
              >
                Decline
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void handleAccept(inv.id)}
                disabled={busy}
                aria-label={`Accept invite to ${title}`}
              >
                Accept
              </Button>
            </Actions>
          </InviteRow>
        );
      })}
    </Block>
  );
}
